/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */
'use strict'

// 3rd party
import React from 'react'

// walrus
import { accessibilityLabel } from '@components/kit/_support'
import { useNavigate } from 'react-router-dom'
import Pressable from '@components/kit/pressable'
import ScrollView, { ScrollViewProps } from '@components/kit/scroll-view'

// lib
import Content from '@components/content'
import Medications from './medications'
import MemberInfo from './member-info'
import MemberPlan from './member-plan'
import { ChatContext } from '../chat.web'

/* -----------------------------------------------------------------------------
 * Profile
 * -------------------------------------------------------------------------- */

export type ProfileContextValue = {
  isEmailPopoverOpen: boolean
  isPhonePopoverOpen: boolean
  isConditionsPopoverOpen: boolean
  isAddInfoPopoverOpen: boolean
  isAllergyPopoverOpen: boolean
  openEmailPopover: () => void
  openPhonePopover: () => void
  openConditionsPopover: () => void
  openAddInfoPopover: () => void
  openAllergyPopover: () => void
  closePopovers: () => void
}

export const ProfileContext = React.createContext<ProfileContextValue>(null)

export type ProfileProps = ScrollViewProps & {
  openEvents: () => void
}

function Profile({ openEvents, ...rest }: ProfileProps) {
  const { userId } = React.useContext(ChatContext)
  const navigate = useNavigate()
  const [
    {
      isEmailPopoverOpen,
      isPhonePopoverOpen,
      isConditionsPopoverOpen,
      isAddInfoPopoverOpen,
      isAllergyPopoverOpen
    },
    setPopoversOpen
  ] = React.useState<{
    isEmailPopoverOpen: boolean
    isPhonePopoverOpen: boolean
    isConditionsPopoverOpen: boolean
    isAddInfoPopoverOpen: boolean
    isAllergyPopoverOpen: boolean
  }>({
    isEmailPopoverOpen: false,
    isPhonePopoverOpen: false,
    isConditionsPopoverOpen: false,
    isAddInfoPopoverOpen: false,
    isAllergyPopoverOpen: false
  })

  const openEmailPopover = () => {
    setPopoversOpen({
      isEmailPopoverOpen: true,
      isPhonePopoverOpen: false,
      isConditionsPopoverOpen: false,
      isAddInfoPopoverOpen: false,
      isAllergyPopoverOpen: false
    })
  }

  const openPhonePopover = () => {
    setPopoversOpen({
      isEmailPopoverOpen: false,
      isPhonePopoverOpen: true,
      isConditionsPopoverOpen: false,
      isAddInfoPopoverOpen: false,
      isAllergyPopoverOpen: false
    })
  }

  const openConditionsPopover = () => {
    setPopoversOpen({
      isEmailPopoverOpen: false,
      isPhonePopoverOpen: false,
      isConditionsPopoverOpen: true,
      isAddInfoPopoverOpen: false,
      isAllergyPopoverOpen: false
    })
  }

  const openAddInfoPopover = () => {
    setPopoversOpen({
      isEmailPopoverOpen: false,
      isPhonePopoverOpen: false,
      isConditionsPopoverOpen: false,
      isAddInfoPopoverOpen: true,
      isAllergyPopoverOpen: false
    })
  }

  const openAllergyPopover = () => {
    setPopoversOpen({
      isEmailPopoverOpen: false,
      isPhonePopoverOpen: false,
      isConditionsPopoverOpen: false,
      isAddInfoPopoverOpen: false,
      isAllergyPopoverOpen: true
    })
  }

  const closePopovers = () => {
    setPopoversOpen({
      isEmailPopoverOpen: false,
      isPhonePopoverOpen: false,
      isConditionsPopoverOpen: false,
      isAddInfoPopoverOpen: false,
      isAllergyPopoverOpen: false
    })
  }

  return (
    <ProfileContext.Provider
      value={{
        isEmailPopoverOpen,
        isPhonePopoverOpen,
        isConditionsPopoverOpen,
        isAddInfoPopoverOpen,
        isAllergyPopoverOpen,
        openEmailPopover,
        openPhonePopover,
        openConditionsPopover,
        openAddInfoPopover,
        openAllergyPopover,
        closePopovers
      }}
    >
      <ScrollView {...accessibilityLabel('Profile')} {...rest}>
        <Content>
          <MemberInfo openEvents={openEvents} />
          <MemberPlan />
          <Medications />
        </Content>
      </ScrollView>
      {(isEmailPopoverOpen ||
        isPhonePopoverOpen ||
        isConditionsPopoverOpen ||
        isAddInfoPopoverOpen ||
        isAllergyPopoverOpen) && (
        <Pressable
          position='fixed'
          top={0}
          left={0}
          width='100%'
          height='100%'
          onPress={closePopovers}
        />
      )}
    </ProfileContext.Provider>
  )
}

export default Profile
